'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import { Delete01Icon } from '@terra/ui-icons';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';
import { SettingsEmptyState } from '../../settings/SettingsEmptyState';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsSection } from '../../settings/SettingsSection';
import { TableGrid, type TableGridRow } from '../../table-grid/TableGrid';
import {
  resolveAttackPathEntityName,
  type InternalNetworkAdEntityKind,
  type InternalNetworkAttackPathEntity,
} from './internal-scope.model';

export type InternalNetworkAttackPathEntityTableProps = {
  title: string;
  description: string;
  entities: InternalNetworkAttackPathEntity[];
  nameColumnTitle: string;
  searchPlaceholder: string;
  emptyStateTitle: string;
  emptyStateDescription: string;
  removeLabel: string;
  onRemove: (entity: InternalNetworkAttackPathEntity) => void;
  isLoading?: boolean;
  disabled?: boolean;
  tableMaxHeight?: string;
};

export function InternalNetworkAttackPathEntityTable({
  title,
  description,
  entities,
  nameColumnTitle,
  searchPlaceholder,
  emptyStateTitle,
  emptyStateDescription,
  removeLabel,
  onRemove,
  isLoading,
  disabled,
  tableMaxHeight = '50vh',
}: InternalNetworkAttackPathEntityTableProps) {
  const t = useTranslations('designSystem.networkSettings.internalScope');
  const tSurfaceScope = useTranslations('designSystem.surfaceScope');
  const rows = useMemo<TableGridRow[]>(() => {
    const entityKindLabel = (entityKind: InternalNetworkAdEntityKind) =>
      t(`attackPath.entityKinds.${entityKind}`);

    return entities.map((entity) => {
      const name = resolveAttackPathEntityName(entity);

      return {
        id: entity.id,
        value: entity.entityId,
        cells: [
          <Tooltip key={`${entity.id}-label`} title={name}>
            <Box truncate maxW="full">
              {name}
            </Box>
          </Tooltip>,
          entityKindLabel(entity.entityKind),
          <HStack
            key={`${entity.id}-actions`}
            gap={2}
            justifyContent="flex-end"
            alignItems="center"
            height="full"
          >
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={removeLabel}
              onClick={() => onRemove(entity)}
              disabled={disabled}
            >
              <Delete01Icon size={16} />
            </IconButton>
          </HStack>,
        ],
      };
    });
  }, [disabled, entities, onRemove, removeLabel, t]);

  return (
    <SettingsSection
      title={`${title} (${entities.length})`}
      description={description}
    >
      <SettingsCard>
        <TableGrid
          tableMaxHeight={tableMaxHeight}
          isLoading={isLoading}
          columns={[
            {
              id: 'name',
              title: nameColumnTitle,
              width: 'minmax(0, 1fr)',
            },
            {
              id: 'entityKind',
              title: t('attackPath.entityKindColumn'),
              width: '160px',
            },
            {
              id: 'actions',
              title: tSurfaceScope('actionsColumn'),
              width: '80px',
            },
          ]}
          rows={rows}
          placeholder={searchPlaceholder}
          emptyState={
            <SettingsEmptyState>
              {emptyStateDescription ?? emptyStateTitle}
            </SettingsEmptyState>
          }
        />
      </SettingsCard>
    </SettingsSection>
  );
}
